import { JobOption } from 'pages/apps/tasks/types/formFields';

// Types
type JetRow = { id: string; count: string; size: string };
type Constants = { C1: string; C2: string; C3: string; C4: string; PI: string };
type ResultsTable = Record<string, number>;
type ScenarioResult = {
  flow: number;
  label: string;
  deltaLabel: string;
  bit: ResultsTable;
  ur: ResultsTable;
};
type ResultsState = {
  totalTFA: number | null;
  scenarios: ScenarioResult[];
};

type JetRowErrors = {
  count: boolean;
  size: boolean;
};

type JetErrorsMap = Record<string, JetRowErrors>;

interface JetRowItemProps {
  row: JetRow;
  index: number;
  onChangeCount: (id: string, value: string) => void;
  onChangeSize: (id: string, value: string) => void;
  onBlurSize?: (id: string) => void;
  countError?: boolean;
  sizeError?: boolean;
  onRemove?: (id: string) => void;
  disableRemove?: boolean;
  disabled?: boolean;
}

type PressureDropJobOption = JobOption & { jobTypeName?: string };

type MetricDescriptor = {
  key: string;
  label: string;
};

type PressureDropSeriesPoint = {
  flow: number;
  urPressureDrop: number;
  bitPressureDrop: number;
  deltaLabel: string;
  isBase: boolean;
  bitFlow: number;
  urFlow: number;
  scenarioIndex: number;
};

type JetRowSnapshot = {
  count: string;
  size: string;
};

type CalculatorInputsSnapshot = {
  mudWt: string;
  flowRate: string;
  comparisonRate: string;
  bitDiameter: string;
  urDiameter: string;
  bitJets: JetRowSnapshot[];
  urJets: JetRowSnapshot[];
  bitJetsBlank: boolean;
  urJetsBlank: boolean;
  toolType: 'BR' | 'DTU' | 'DT' | 'DTS' | 'DDT';
  toolSeries: string;
  isGenericMode: boolean;
  constants: Constants;
  selectedJobId: string;
  selectedJobLabel: string;
};

type TimelineSummaryItem = {
  key: string;
  label: string;
  value: string;
};

type TimelineDiffItem = TimelineSummaryItem & {
  previousValue: string;
  changed: boolean;
};

type TimelineEntry = {
  id: string;
  label: string;
  createdAtISO: string;
  createdAtLabel: string;
  generatedBy: string;
  inputs: CalculatorInputsSnapshot;
  results: ResultsState;
  parameterAlerts: string[];
};

type ToolSeriesLimits = {
  maxFlowArea: number;
  pressureMin: number;
  pressureMax: number;
};

type AdaptivePalette = {
  bitPressure: string;
  urPressure: string;
  bitFlow: string;
  urFlow: string;
  accent: string;
  gradientStops: [string, string];
};

const BIT_METRICS: readonly MetricDescriptor[] = [
  { key: 'TFA (In2)', label: 'TFA (in²)' },
  { key: '% Flow', label: '% Flow' },
  { key: 'Flow', label: 'Flow (gpm)' },
  { key: 'Hole Area', label: 'Hole Area (in²)' },
  { key: '% Fm removed by Bit', label: '% Fm removed by Bit' },
  { key: 'Pressure Drop (PSI)', label: 'Pressure Drop (PSI)' },
  { key: 'HHP', label: 'HHP' },
  { key: 'HHPSI', label: 'HHPSI' },
  { key: 'Jet Velocity', label: 'Jet Velocity (ft/sec)' },
  { key: 'Jet Impact Force', label: 'Jet Impact Force (lbf)' }
] as const;

const UR_METRICS: readonly MetricDescriptor[] = [
  { key: 'TFA (In2)', label: 'TFA (in²)' },
  { key: '% Flow', label: '% Flow' },
  { key: 'Flow', label: 'Flow (gpm)' },
  { key: 'Hole Area', label: 'Hole Area (in²)' },
  { key: '% Fm removed by UR', label: '% Fm removed by UR' },
  { key: 'Pressure Drop (PSI)', label: 'Pressure Drop (PSI)' },
  { key: 'HHP', label: 'HHP' },
  { key: 'HHPSI', label: 'HHPSI' },
  { key: 'Jet Velocity', label: 'Jet Velocity (ft/sec)' },
  { key: 'Jet Impact Force', label: 'Jet Impact Force (lbf)' }
] as const;

const FIXED_TWO_DECIMAL_METRICS = new Set([
  'Hole Area',
  '% Fm removed by Bit',
  '% Fm removed by UR'
]);

const TOOL_TYPE_OPTIONS: {
  value: 'BR' | 'DTU' | 'DT' | 'DTS' | 'DDT';
  label: string;
}[] = [
  { value: 'BR', label: 'BR' },
  { value: 'DTU', label: 'DTU' },
  { value: 'DT', label: 'DT' },
  { value: 'DTS', label: 'DTS' },
  { value: 'DDT', label: 'DDT' }
];

const TOOL_SERIES_MAP: Record<'BR' | 'DTU' | 'DT' | 'DTS' | 'DDT', string[]> = {
  BR: ['575'],
  DTU: ['575', '725', '800', '950', '1175', '1450', '1600', '2200'],
  DT: ['350'],
  DTS: ['425'],
  DDT: ['425', '575', '725', '825']
};

const TOOL_LIMITS: Record<
  'BR' | 'DTU' | 'DT' | 'DTS' | 'DDT',
  Record<string, ToolSeriesLimits>
> = {
  BR: {
    '575': { maxFlowArea: 0.54, pressureMin: 250, pressureMax: 1100 }
  },
  DTU: {
    '575': { maxFlowArea: 0.31, pressureMin: 250, pressureMax: 1100 },
    '725': { maxFlowArea: 0.54, pressureMin: 250, pressureMax: 1100 },
    '800': { maxFlowArea: 0.54, pressureMin: 250, pressureMax: 1100 },
    '950': { maxFlowArea: 1.23, pressureMin: 250, pressureMax: 2000 },
    '1175': { maxFlowArea: 1.23, pressureMin: 250, pressureMax: 2000 },
    '1450': { maxFlowArea: 2.78, pressureMin: 500, pressureMax: 2000 },
    '1600': { maxFlowArea: 2.78, pressureMin: 500, pressureMax: 2000 },
    '2200': { maxFlowArea: 2.78, pressureMin: 500, pressureMax: 2000 }
  },
  DT: {
    '350': { maxFlowArea: 0.54, pressureMin: 150, pressureMax: 1100 }
  },
  DTS: {
    '425': { maxFlowArea: 0.54, pressureMin: 150, pressureMax: 1100 }
  },
  DDT: {
    '425': { maxFlowArea: 0.31, pressureMin: 150, pressureMax: 1100 },
    '575': { maxFlowArea: 0.54, pressureMin: 150, pressureMax: 1100 },
    '725': { maxFlowArea: 0.86, pressureMin: 250, pressureMax: 1100 },
    '825': { maxFlowArea: 0.86, pressureMin: 250, pressureMax: 2000 }
  }
};

const ONBOARDING_STORAGE_KEY = 'hoc-pressure-drop-onboarding-dismissed';

type ShearScrewDefaults = {
  screws: number;
  psiToMoveArms: number;
  pistonArea: number;
};

const SHEAR_SCREW_DEFAULTS: Record<string, ShearScrewDefaults> = {
  'BR 575': { screws: 0, psiToMoveArms: 0, pistonArea: 8.227 },
  'DTU 1175': { screws: 0, psiToMoveArms: 0, pistonArea: 28.824 },
  'DTU 1450': { screws: 0, psiToMoveArms: 40, pistonArea: 36.06 },
  'DTU 1600': { screws: 0, psiToMoveArms: 50, pistonArea: 42.536 },
  'DTU 2200': { screws: 5, psiToMoveArms: 65, pistonArea: 42.536 }
};

const SHEAR_SCREW_TOOL_KEYS = new Set(Object.keys(SHEAR_SCREW_DEFAULTS));

const TOOL_THEME_PRESETS: Record<string, AdaptivePalette> = {
  DEFAULT: {
    bitPressure: '#1f8efa',
    urPressure: '#5a5afc',
    bitFlow: '#16c77b',
    urFlow: '#f7a53a',
    accent: '#ef6d9f',
    gradientStops: ['#1f8efa', '#111827']
  },
  BR: {
    bitPressure: '#00a5ff',
    urPressure: '#2d6af5',
    bitFlow: '#00d68f',
    urFlow: '#f8c146',
    accent: '#ff7aa2',
    gradientStops: ['#00a5ff', '#132236']
  },
  DTU: {
    bitPressure: '#f97316',
    urPressure: '#facc15',
    bitFlow: '#84cc16',
    urFlow: '#22d3ee',
    accent: '#fb7185',
    gradientStops: ['#f97316', '#1e1836']
  },
  DT: {
    bitPressure: '#22d3ee',
    urPressure: '#6366f1',
    bitFlow: '#34d399',
    urFlow: '#fbbf24',
    accent: '#f472b6',
    gradientStops: ['#22d3ee', '#0d182d']
  },
  DTS: {
    bitPressure: '#a855f7',
    urPressure: '#3b82f6',
    bitFlow: '#22d3ee',
    urFlow: '#fcd34d',
    accent: '#ec4899',
    gradientStops: ['#a855f7', '#17122b']
  },
  DDT: {
    bitPressure: '#ef4444',
    urPressure: '#6366f1',
    bitFlow: '#14b8a6',
    urFlow: '#f97316',
    accent: '#f43f5e',
    gradientStops: ['#ef4444', '#221523']
  }
};

// Defaults
const DEFAULT_CONSTANTS: Constants = {
  C1: '10858',
  C2: '1714',
  C3: '0.320832',
  C4: '0.000516',
  PI: '3.14159265358979'
};

export type {
  JetRow,
  Constants,
  ResultsTable,
  ScenarioResult,
  ResultsState,
  JetRowErrors,
  JetErrorsMap,
  JetRowItemProps,
  PressureDropJobOption,
  MetricDescriptor,
  PressureDropSeriesPoint,
  JetRowSnapshot,
  CalculatorInputsSnapshot,
  TimelineSummaryItem,
  TimelineDiffItem,
  TimelineEntry,
  ToolSeriesLimits,
  AdaptivePalette
};

export type { ShearScrewDefaults };

export {
  BIT_METRICS,
  UR_METRICS,
  FIXED_TWO_DECIMAL_METRICS,
  TOOL_TYPE_OPTIONS,
  TOOL_SERIES_MAP,
  TOOL_LIMITS,
  ONBOARDING_STORAGE_KEY,
  TOOL_THEME_PRESETS,
  DEFAULT_CONSTANTS,
  SHEAR_SCREW_DEFAULTS,
  SHEAR_SCREW_TOOL_KEYS
};
